import { makeAutoObservable, runInAction, when } from 'mobx';
import { Subscription } from 'rxjs';
import { logger } from '@cutwise/utils';
import { arrayMove } from '@dnd-kit/sortable';

import useStore from '@/common/hooks/useStore';
import { DriveAPI } from '@/drive/api/DriveAPI';
import { DriveContainerId, DriveItemId } from '@/drive/entities/DriveItemId';
import { DriveNode } from '@/drive/entities/DriveNode';
import { DriveNodeTree } from '@/drive/entities/DriveNodeTree';
import { canBeUsedInTree, toTreeNode } from '@/drive/entities/DriveTreeItem/DriveTreeItem';
import {
  DriveModifierStore,
  driveModifierStore as globalDriveModifierStore,
} from '@/drive/stores/DriveModifierStore/DriveModifierStore';
import {
  DriveModifierEvent,
  DriveModifierEventType,
  DriveMoveApplyEvent,
  DriveMoveSuccessEvent,
  DriveUpdateApplyEvent,
  OptimisticUpdatePhase,
} from '@/drive/stores/DriveModifierStore/DriveModifierStore.types';
import { getSavedItemByTempId } from '@/drive/stores/DriveModifierStore/DriveModifierStore.utils';
import { DriveDragData } from '@/drive/types/DriveDragData';
import { DriveInsertion, toInsertionIndex } from '@/drive/types/DriveInsertion';

class DriveTreeStore {
  isLoading = false;

  private nodeTree: Nullable<DriveNodeTree> = null;

  private subscription: Nullable<Subscription> = null;

  constructor(
    private driveModifierStore: DriveModifierStore,
    private driveAPI = new DriveAPI(),
  ) {
    makeAutoObservable(this);

    this.subscription = this.driveModifierStore.event$.subscribe(this.handleModifierEvent);
  }

  get tree(): Nullable<DriveNode[]> {
    return this.nodeTree?.roots ?? null;
  }

  refresh = async () => {
    if (this.isLoading) {
      return when(() => !this.isLoading);
    }

    this.isLoading = true;

    try {
      const treeResult = await this.driveAPI.fetchTree();

      runInAction(() => {
        this.nodeTree = new DriveNodeTree(treeResult);
      });
    } catch (e) {
      logger.error(e);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  reorderItems = async (drag: DriveDragData, insertion: DriveInsertion) => {
    if (drag.driveItem.id === insertion.targetId) {
      return;
    }

    const { parentId } = drag;
    const siblings = this.nodeTree?.getChildren(parentId);

    if (!siblings) {
      return;
    }

    const oldIndex = siblings.findIndex((node) => node.id === drag.driveItem.id);
    const targetIndex = siblings.findIndex((node) => node.id === insertion.targetId);

    if (oldIndex === -1 || targetIndex === -1) {
      return;
    }

    const newIndex = toInsertionIndex(insertion, oldIndex, targetIndex);

    if (newIndex === oldIndex) {
      return;
    }

    const reorderedSiblings = arrayMove(siblings, oldIndex, newIndex);

    await this.driveModifierStore.reorder(
      parentId,
      reorderedSiblings.map((node) => node.id),
    );
  };

  insertItemAt = async (itemId: DriveItemId, parentId: DriveContainerId, insertion: DriveInsertion) => {
    const siblings = this.nodeTree?.getChildren(parentId);

    if (!siblings) {
      return;
    }

    const otherIds = siblings.map((node) => node.id).filter((id) => id !== itemId);
    const targetIndex = otherIds.indexOf(insertion.targetId);

    if (targetIndex === -1) {
      return;
    }

    const slotIndex = insertion.isAfter ? targetIndex + 1 : targetIndex;

    await this.driveModifierStore.reorder(parentId, [
      ...otherIds.slice(0, slotIndex),
      itemId,
      ...otherIds.slice(slotIndex),
    ]);
  };

  dispose = () => {
    this.subscription?.unsubscribe();
    this.subscription = null;
    this.nodeTree = null;
  };

  private handleModifierEvent = (event: DriveModifierEvent) => {
    const { nodeTree } = this;

    if (!nodeTree) {
      return;
    }

    if (event.phase === OptimisticUpdatePhase.Error) {
      this.refresh();

      return;
    }

    if (event.type === DriveModifierEventType.Add) {
      if (event.phase === OptimisticUpdatePhase.Apply) {
        const temporaryNodes = event.temporaryItems.filter(canBeUsedInTree).map((item) => item.toNode());

        nodeTree.insertTemporaryNodes(event.parentId, temporaryNodes, event.precedingNodeIdByTempId);
      } else if (event.hasOptimisticData) {
        nodeTree.resolveTemporaryNodes(event.parentId, getSavedItemByTempId(event.items, toTreeNode));
      } else {
        this.refresh();
      }

      return;
    }

    if (event.type === DriveModifierEventType.AccessChange) {
      this.refresh();

      return;
    }

    if (event.type === DriveModifierEventType.Move) {
      if (event.phase === OptimisticUpdatePhase.Apply) {
        this.handleMoveApply(nodeTree, event);
      } else {
        this.handleMoveSuccess(nodeTree, event);
      }

      return;
    }

    if (event.type === DriveModifierEventType.Update && event.phase === OptimisticUpdatePhase.Apply) {
      this.handleUpdateApply(nodeTree, event);
    } else if (event.type === DriveModifierEventType.Delete && event.phase === OptimisticUpdatePhase.Apply) {
      nodeTree.removeNode(event.item.id);
    } else if (event.type === DriveModifierEventType.Reorder && event.phase === OptimisticUpdatePhase.Apply) {
      nodeTree.reorder(event.parentId, event.orderedIds);
    }
  };

  private handleMoveApply(nodeTree: DriveNodeTree, event: DriveMoveApplyEvent) {
    const node = toTreeNode(event.item);

    if (node) {
      nodeTree.move(node, event.parentId, event.previousParentId);
    }
  }

  private handleMoveSuccess(nodeTree: DriveNodeTree, event: DriveMoveSuccessEvent) {
    const node = toTreeNode(event.backendItem);

    if (!node) {
      return;
    }

    if (!nodeTree.hasNode(event.parentId) || !nodeTree.hasNode(node.id)) {
      this.refresh();
    } else {
      nodeTree.updateNode(node);
    }
  }

  private handleUpdateApply(nodeTree: DriveNodeTree, event: DriveUpdateApplyEvent) {
    const { id, title, description } = event.patch;
    const node = nodeTree.findNode(id);

    if (node) {
      nodeTree.updateNode({ ...node, title, description });
    }
  }
}

export const driveTreeStore = new DriveTreeStore(globalDriveModifierStore);

export const useDriveTreeStore = () => useStore(DriveTreeStore);

export default DriveTreeStore;
